// Frame: y-up. The boat's nose is local -z, and it flies toward world -z.
// The camera sits behind the boat (+z) and looks the same way.

import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import {
  BEAT_Z,
  START_Y,
  START_Z,
  buildRun,
  nextRing,
  pathX,
  pathY,
  stepRun,
} from "./sim.js";
import { createAudio } from "./audio.js";

const MOON = new THREE.Vector3(-0.28, 0.14, -0.78).normalize();
const SAVE_KEY = "lumen-tide-save-v1";
const WAKES = {
  gold: { trail: [1.0, 0.78, 0.46], lamp: [2.1, 1.15, 0.48], label: "Gold" },
  pearl: { trail: [0.78, 0.88, 1.0], lamp: [0.9, 1.15, 1.7], label: "Pearl" },
  aurora: { trail: [0.5, 0.95, 0.7], lamp: [0.55, 1.7, 0.85], label: "Aurora" },
};

const canvas = document.getElementById("c");
const audio = createAudio();
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const el = {
  title: document.getElementById("title"),
  card: document.getElementById("card"),
  paused: document.getElementById("paused"),
  hud: document.getElementById("hud"),
  hint: document.getElementById("hint"),
  fail: document.getElementById("fail"),
  score: document.getElementById("points"),
  chain: document.getElementById("chain"),
  pips: [...document.querySelectorAll("#pips i")],
  best: document.getElementById("best"),
  kicker: document.getElementById("card-kicker"),
  cardTitle: document.getElementById("card-title"),
  cardScore: document.getElementById("card-score"),
  detail: document.getElementById("card-detail"),
  further: document.getElementById("further"),
  hear: document.getElementById("hear"),
  copy: document.getElementById("copy"),
  pause: document.getElementById("pause"),
  mute: document.getElementById("mute"),
  wake: document.getElementById("wake"),
};

function show(node, on) {
  node.hidden = !on;
}

function todayKey() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function freshSave() {
  return { best: 0, chainBest: 0, pearl: false, aurora: false, wake: "gold", daily: null, runs: 0, mute: false };
}

function loadSave() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    return raw ? { ...freshSave(), ...JSON.parse(raw) } : freshSave();
  } catch {
    return freshSave();
  }
}

let save = loadSave();

function persist() {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(save));
  } catch {
    /* private mode can refuse storage */
  }
}

function wakeUnlocked(name) {
  if (name === "gold") return true;
  if (name === "pearl") return !!save.pearl;
  if (name === "aurora") return !!save.aurora;
  return false;
}

function wakeName() {
  return wakeUnlocked(save.wake) ? save.wake : save.aurora ? "aurora" : save.pearl ? "pearl" : "gold";
}

let wake = WAKES[wakeName()];

function fail(message) {
  el.fail.hidden = false;
  el.fail.textContent = message || "This sea needs WebGL. Try Chrome or Brave.";
}

let renderer;
try {
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: "high-performance",
  });
} catch (err) {
  fail();
  throw err;
}
if (!renderer.getContext()) fail();

renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, window.innerWidth < 800 ? 1.25 : 1.6));
renderer.setSize(window.innerWidth, window.innerHeight, false);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.98;
renderer.setClearColor(0x070b12, 1);

const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0x101820, 0.01);
const camera = new THREE.PerspectiveCamera(47, window.innerWidth / window.innerHeight, 0.1, 900);
camera.position.set(0, 6, 18);

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.2, 0.22, 1.02);
composer.addPass(bloom);
composer.addPass(new OutputPass());

scene.add(new THREE.HemisphereLight(0x8ea4c4, 0x14100e, 0.42));
const moonLight = new THREE.DirectionalLight(0xe4eeff, 1.85);
moonLight.position.copy(MOON).multiplyScalar(40);
scene.add(moonLight);
const fill = new THREE.DirectionalLight(0xc5d4ea, 0.9);
fill.position.set(1.4, 4.2, 18);
scene.add(fill);

const sky = new THREE.Mesh(
  new THREE.SphereGeometry(520, 48, 32),
  new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      uTime: { value: 0 },
      uHymn: { value: 0 },
      uMoon: { value: MOON.clone() },
    },
    vertexShader: `
      varying vec3 vDir;
      void main() {
        vDir = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      varying vec3 vDir;
      uniform float uTime;
      uniform float uHymn;
      uniform vec3 uMoon;
      float hash(vec3 p) {
        p = fract(p * 0.3183099 + vec3(0.11, 0.17, 0.13));
        p *= 17.0;
        return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
      }
      void main() {
        vec3 dir = normalize(vDir);
        float h = dir.y;
        vec3 zenith = vec3(0.003, 0.006, 0.018);
        vec3 mid = vec3(0.015, 0.03, 0.07);
        vec3 horizon = vec3(0.07, 0.09, 0.13);
        vec3 blush = vec3(0.42, 0.28, 0.16);
        vec3 col = mix(horizon, mid, smoothstep(0.0, 0.28, h));
        col = mix(col, zenith, smoothstep(0.12, 0.72, h));
        vec2 m = normalize(uMoon.xz);
        vec2 d = normalize(dir.xz + vec2(0.0001));
        float under = pow(max(dot(m, d), 0.0), 10.0) * (1.0 - smoothstep(0.0, 0.18, h));
        col = mix(col, blush, under * 0.28);
        float moon = pow(max(dot(dir, normalize(uMoon)), 0.0), 1400.0);
        float glow = pow(max(dot(dir, normalize(uMoon)), 0.0), 28.0);
        col += vec3(1.25, 1.18, 1.0) * moon * 0.55;
        col += vec3(0.55, 0.46, 0.28) * glow * 0.16;
        vec3 starGrid = floor(dir * 340.0);
        vec3 starFract = fract(dir * 340.0) - 0.5;
        float stars = hash(starGrid);
        float tw = 0.65 + 0.35 * sin(uTime * (1.2 + stars * 3.0) + stars * 20.0);
        float star = smoothstep(0.16, 0.0, length(starFract)) * step(0.993, stars) * tw * smoothstep(0.08, 0.28, h);
        col += vec3(0.9, 0.95, 1.0) * star;
        float curtain = sin(dir.x * 6.0 + dir.z * 2.2 + uTime * 0.12);
        curtain += sin(dir.x * 13.0 - uTime * 0.18) * 0.35;
        float band = exp(-pow((dir.y - 0.38 - curtain * 0.07) * 7.5, 2.0));
        band *= smoothstep(0.02, 0.35, dir.y);
        vec3 aurora = mix(vec3(0.2, 0.85, 0.55), vec3(0.95, 0.75, 0.32), curtain * 0.5 + 0.5);
        col += aurora * band * (0.07 + uHymn * 0.5);
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  }),
);
sky.frustumCulled = false;
sky.renderOrder = -1;
scene.add(sky);

const sea = new THREE.Mesh(
  new THREE.PlaneGeometry(680, 680, window.innerWidth < 800 ? 80 : 120, window.innerWidth < 800 ? 80 : 120),
  new THREE.ShaderMaterial({
    fog: false,
    uniforms: {
      uTime: { value: 0 },
      uBoat: { value: new THREE.Vector2() },
      uMoon: { value: MOON.clone() },
      uLamp: { value: new THREE.Color(1, 0.62, 0.28) },
      uLampStrength: { value: 0.28 },
      uFogColor: { value: scene.fog.color },
      uFogDensity: { value: scene.fog.density },
    },
    vertexShader: `
      uniform float uTime;
      varying vec3 vWorld;
      varying vec3 vNormal;
      varying float vHeight;
      float waveH(vec2 xz) {
        float h = 0.0;
        h += sin(xz.x * 0.09 + xz.y * 0.035 + uTime * 0.42) * 0.42;
        h += sin(xz.x * -0.07 + xz.y * 0.16 + uTime * 0.62) * 0.22;
        h += sin(xz.x * 0.31 + xz.y * -0.18 + uTime * 0.95) * 0.08;
        h += sin(xz.x * 1.15 + xz.y * 0.82 + uTime * 1.4) * 0.035;
        return h;
      }
      void main() {
        vec3 pos = position;
        vec4 baseWorld = modelMatrix * vec4(pos, 1.0);
        float h = waveH(baseWorld.xz);
        pos.z += h;
        vec4 world = modelMatrix * vec4(pos, 1.0);
        float e = 0.85;
        float hx = waveH(baseWorld.xz + vec2(e, 0.0));
        float hz = waveH(baseWorld.xz + vec2(0.0, e));
        vNormal = normalize(vec3(-(hx - h) / e * 3.6, 1.0, -(hz - h) / e * 3.6));
        vWorld = world.xyz;
        vHeight = h;
        vec4 mvPosition = viewMatrix * world;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      precision highp float;
      varying vec3 vWorld;
      varying vec3 vNormal;
      varying float vHeight;
      uniform vec2 uBoat;
      uniform vec3 uMoon;
      uniform vec3 uLamp;
      uniform float uLampStrength;
      uniform float uTime;
      uniform vec3 uFogColor;
      uniform float uFogDensity;
      float hash(vec2 p) {
        vec3 p3 = fract(vec3(p.xyx) * 0.1031);
        p3 += dot(p3, p3.yzx + 33.33);
        return fract((p3.x + p3.y) * p3.z);
      }
      void main() {
        vec3 N = normalize(vNormal);
        vec3 V = normalize(cameraPosition - vWorld);
        float ndv = clamp(dot(N, V), 0.0, 1.0);
        float fres = pow(1.0 - ndv, 4.2);
        vec3 deep = vec3(0.004, 0.012, 0.02);
        vec3 midc = vec3(0.012, 0.032, 0.046);
        vec3 col = mix(deep, midc, smoothstep(-0.35, 0.4, vHeight));
        vec3 R = reflect(-V, N);
        float skyH = R.y;
        vec3 skyHit = mix(vec3(0.05, 0.07, 0.1), vec3(0.01, 0.02, 0.05), smoothstep(0.0, 0.45, skyH));
        col = mix(col, skyHit, fres * 0.62);
        vec3 L = normalize(uMoon);
        float glint = pow(max(dot(R, L), 0.0), 420.0);
        col += vec3(0.95, 0.88, 0.72) * glint * 0.7;
        vec2 rel = vWorld.xz - cameraPosition.xz;
        vec2 m = normalize(uMoon.xz);
        float along = dot(rel, m);
        float side = abs(rel.x * m.y - rel.y * m.x);
        float lane = exp(-side * side * 0.09) * smoothstep(-18.0, 6.0, along) * exp(-max(along, 0.0) * 0.008);
        col += vec3(0.75, 0.68, 0.5) * lane * 0.16;
        float d = distance(vWorld.xz, uBoat);
        col += uLamp * exp(-d * d / 3.2) * uLampStrength;
        vec2 gv = vWorld.xz * 3.4;
        vec2 cellId = floor(gv);
        vec2 f = fract(gv) - 0.5;
        float cell = hash(cellId);
        vec2 spot = (vec2(cell, hash(cellId + 3.1)) - 0.5) * 0.4;
        float tw = 0.4 + 0.6 * sin(uTime * 2.4 + cell * 30.0);
        float spark = smoothstep(0.045, 0.0, length(f - spot)) * step(0.985, cell) * tw * fres;
        col += vec3(0.9, 0.95, 1.0) * spark * 0.85;
        float crest = smoothstep(0.22, 0.5, vHeight) * fres;
        col += vec3(0.18, 0.22, 0.26) * crest * 0.45;
        float fogDepth = length(vWorld - cameraPosition);
        float fogFactor = 1.0 - exp(-uFogDensity * uFogDensity * fogDepth * fogDepth);
        col = mix(col, uFogColor, clamp(fogFactor, 0.0, 1.0));
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  }),
);
sea.rotation.x = -Math.PI / 2;
scene.add(sea);

function glowTexture() {
  const picture = document.createElement("canvas");
  picture.width = picture.height = 64;
  const pen = picture.getContext("2d");
  const grd = pen.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0, "rgba(255,255,255,1)");
  grd.addColorStop(0.35, "rgba(255,255,255,0.5)");
  grd.addColorStop(1, "rgba(255,255,255,0)");
  pen.fillStyle = grd;
  pen.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(picture);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

const glowMap = glowTexture();
function moonTexture() {
  const picture = document.createElement("canvas");
  picture.width = picture.height = 128;
  const pen = picture.getContext("2d");
  const grd = pen.createRadialGradient(64, 64, 20, 64, 64, 64);
  grd.addColorStop(0, "rgba(255,252,244,1)");
  grd.addColorStop(0.46, "rgba(255,246,226,1)");
  grd.addColorStop(0.52, "rgba(255,236,200,0.9)");
  grd.addColorStop(0.7, "rgba(255,214,160,0.18)");
  grd.addColorStop(1, "rgba(255,214,160,0)");
  pen.fillStyle = grd;
  pen.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(picture);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const moonBody = new THREE.Sprite(
  new THREE.SpriteMaterial({
    map: moonTexture(),
    color: new THREE.Color().setRGB(1.15, 1.1, 0.96),
    transparent: true,
    depthWrite: false,
    fog: false,
  }),
);
moonBody.scale.set(34, 34, 1);
moonBody.material.opacity = 0.28;
moonBody.material.blending = THREE.AdditiveBlending;
scene.add(moonBody);

const moonMesh = new THREE.Mesh(
  new THREE.SphereGeometry(9.2, 40, 28),
  new THREE.ShaderMaterial({
    fog: false,
    depthWrite: false,
    vertexShader: `
      varying vec3 vPos;
      varying vec3 vN;
      void main() {
        vPos = position;
        vN = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      varying vec3 vPos;
      varying vec3 vN;
      float hash(vec3 p) {
        p = fract(p * 0.3183099 + vec3(0.11, 0.17, 0.13));
        p *= 17.0;
        return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
      }
      void main() {
        vec3 n = normalize(vPos);
        float crater = hash(floor(n * 14.0));
        float pit = smoothstep(0.62, 0.9, crater);
        vec3 col = mix(vec3(0.96, 0.93, 0.84), vec3(0.68, 0.66, 0.6), pit * 0.85);
        float face = clamp(-vN.z, 0.0, 1.0);
        col *= 0.72 + 0.28 * face;
        col += vec3(0.08, 0.06, 0.03) * pow(1.0 - face, 1.6);
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  }),
);
scene.add(moonMesh);

const starCount = 900;
const starPositions = new Float32Array(starCount * 3);
const starColors = new Float32Array(starCount * 3);
for (let i = 0; i < starCount; i++) {
  const yaw = Math.random() * Math.PI * 2;
  const y = 0.08 + Math.random() * 0.9;
  const ring = Math.sqrt(Math.max(0, 1 - y * y));
  const radius = 430;
  starPositions[i * 3] = Math.cos(yaw) * ring * radius;
  starPositions[i * 3 + 1] = y * radius;
  starPositions[i * 3 + 2] = Math.sin(yaw) * ring * radius;
  const tint = 0.75 + Math.random() * 0.25;
  starColors[i * 3] = tint;
  starColors[i * 3 + 1] = tint * (0.92 + Math.random() * 0.08);
  starColors[i * 3 + 2] = tint;
}
const starGeo = new THREE.BufferGeometry();
starGeo.setAttribute("position", new THREE.BufferAttribute(starPositions, 3));
starGeo.setAttribute("color", new THREE.BufferAttribute(starColors, 3));
const stars = new THREE.Points(
  starGeo,
  new THREE.PointsMaterial({
    size: 1.7,
    sizeAttenuation: false,
    vertexColors: true,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
    fog: false,
  }),
);
scene.add(stars);

function paperTexture() {
  const picture = document.createElement("canvas");
  picture.width = picture.height = 256;
  const pen = picture.getContext("2d");
  pen.fillStyle = "#f3e4cc";
  pen.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 1800; i++) {
    const shade = 180 + Math.random() * 60;
    pen.fillStyle = `rgba(${shade}, ${shade - 12}, ${shade - 28}, 0.18)`;
    pen.fillRect(Math.random() * 256, Math.random() * 256, 1 + Math.random() * 2, 1);
  }
  pen.strokeStyle = "rgba(120, 84, 48, 0.28)";
  pen.lineWidth = 2;
  pen.beginPath();
  pen.moveTo(20, 40);
  pen.lineTo(230, 210);
  pen.moveTo(30, 200);
  pen.lineTo(210, 30);
  pen.stroke();
  const tex = new THREE.CanvasTexture(picture);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}
const paperMap = paperTexture();

function paperMesh(points, faces) {
  const positions = [];
  for (const face of faces) {
    for (const index of face) positions.push(...points[index]);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  const uvs = [];
  for (const face of faces) {
    for (const index of face) {
      const point = points[index];
      uvs.push((point[0] + 1.3) / 2.6, (point[2] + 1.5) / 3);
    }
  }
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.computeVertexNormals();
  const material = new THREE.MeshStandardMaterial({
    map: paperMap,
    color: 0xfff4e4,
    roughness: 0.72,
    metalness: 0.0,
    emissive: 0x2a1c10,
    emissiveIntensity: 0.08,
    side: THREE.DoubleSide,
    flatShading: true,
  });
  return new THREE.Mesh(geometry, material);
}

function buildBoat() {
  const group = new THREE.Group();
  const id = {};
  const pts = [];
  const V = (name, x, y, z) => {
    id[name] = pts.length;
    pts.push([x, y, z]);
  };
  V("bow", 0, 0.22, -1.34);
  V("stern", 0, 0.2, 1.02);
  V("rimFL", -0.66, 0.48, -0.36);
  V("rimFR", 0.66, 0.48, -0.36);
  V("rimBL", -0.58, 0.46, 0.5);
  V("rimBR", 0.58, 0.46, 0.5);
  V("botFL", -0.32, -0.02, -0.3);
  V("botFR", 0.32, -0.02, -0.3);
  V("botBL", -0.28, 0.0, 0.38);
  V("botBR", 0.28, 0.0, 0.38);
  V("inFL", -0.4, 0.18, -0.18);
  V("inFR", 0.4, 0.18, -0.18);
  V("inBL", -0.36, 0.16, 0.34);
  V("inBR", 0.36, 0.16, 0.34);
  V("peak", 0, 0.86, 0.02);
  const F = (...names) => names.map((name) => id[name]);
  const hull = paperMesh(pts, [
    F("botFL", "botBL", "botBR"),
    F("botFL", "botBR", "botFR"),
    F("bow", "botFL", "botFR"),
    F("stern", "botBR", "botBL"),
    F("bow", "rimFL", "botFL"),
    F("rimFL", "rimBL", "botBL"),
    F("rimFL", "botBL", "botFL"),
    F("rimBL", "stern", "botBL"),
    F("bow", "botFR", "rimFR"),
    F("rimFR", "botFR", "botBR"),
    F("rimFR", "botBR", "rimBR"),
    F("rimBR", "botBR", "stern"),
    F("rimFL", "rimFR", "inFR"),
    F("rimFL", "inFR", "inFL"),
    F("rimFR", "rimBR", "inBR"),
    F("rimFR", "inBR", "inFR"),
    F("rimBR", "rimBL", "inBL"),
    F("rimBR", "inBL", "inBR"),
    F("rimBL", "rimFL", "inFL"),
    F("rimBL", "inFL", "inBL"),
    F("inFL", "inFR", "inBR"),
    F("inFL", "inBR", "inBL"),
    F("peak", "inFL", "inBL"),
    F("peak", "inBR", "inFR"),
    F("peak", "inBL", "inBR"),
  ]);
  hull.name = "hull";
  group.add(hull);

  const mast = new THREE.Mesh(
    new THREE.CylinderGeometry(0.012, 0.014, 0.72, 6),
    hull.material,
  );
  mast.position.set(0, 0.78, -0.02);
  group.add(mast);

  const flagGeo = new THREE.BufferGeometry();
  flagGeo.setAttribute(
    "position",
    new THREE.Float32BufferAttribute([0, 1.12, -0.02, 0.28, 1.02, 0.04, 0, 0.94, -0.02], 3),
  );
  flagGeo.computeVertexNormals();
  const flag = new THREE.Mesh(
    flagGeo,
    new THREE.MeshStandardMaterial({
      color: 0xf7f1e4,
      side: THREE.DoubleSide,
      roughness: 0.6,
      emissive: 0x4a3218,
      emissiveIntensity: 0.2,
    }),
  );
  group.add(flag);

  const lantern = new THREE.Group();
  lantern.position.set(0, 0.4, 0.46);
  const globe = new THREE.Mesh(
    new THREE.CylinderGeometry(0.09, 0.11, 0.22, 6),
    new THREE.MeshBasicMaterial({ color: new THREE.Color().setRGB(2.1, 1.15, 0.48) }),
  );
  globe.name = "globe";
  lantern.add(globe);
  const cap = new THREE.Mesh(
    new THREE.ConeGeometry(0.1, 0.08, 6),
    new THREE.MeshStandardMaterial({ color: 0xe7d7b8, roughness: 0.6 }),
  );
  cap.position.y = 0.14;
  lantern.add(cap);
  const halo = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: glowMap,
      color: 0xffc27a,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  halo.scale.set(0.72, 0.72, 0.72);
  halo.name = "halo";
  lantern.add(halo);
  const lamp = new THREE.PointLight(0xffb15a, 1.1, 8, 2);
  lamp.name = "lamp";
  lantern.add(lamp);
  group.add(lantern);

  const bow = new THREE.Object3D();
  bow.name = "bow";
  bow.position.set(0, 0.22, -1.34);
  group.add(bow);
  group.scale.setScalar(1.45);
  return group;
}

const boat = buildBoat();
scene.add(boat);
const globeMat = boat.getObjectByName("globe").material;
const halo = boat.getObjectByName("halo");
const lamp = boat.getObjectByName("lamp");

const shadow = new THREE.Mesh(
  new THREE.CircleGeometry(0.85, 24),
  new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.28, depthWrite: false }),
);
shadow.rotation.x = -Math.PI / 2;
scene.add(shadow);

function rockGeometry(seed) {
  const geometry = new THREE.IcosahedronGeometry(1, 2);
  const position = geometry.attributes.position;
  let value = seed >>> 0;
  const rand = () => {
    value = (value + 0x6d2b79f5) | 0;
    let t = Math.imul(value ^ (value >>> 15), 1 | value);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    const len = Math.hypot(x, y, z) || 1;
    let wobble = 0.85 + rand() * 0.28;
    if (y / len < 0.15) wobble *= 0.55;
    position.setXYZ(i, x * wobble, y * wobble * 0.78, z * wobble);
  }
  geometry.computeVertexNormals();
  const colors = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i++) {
    const lift = Math.max(0, Math.min(1, (position.getY(i) + 0.25) / 1.15));
    colors[i * 3] = 0.32 + lift * 0.5;
    colors[i * 3 + 1] = 0.36 + lift * 0.46;
    colors[i * 3 + 2] = 0.42 + lift * 0.42;
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geometry;
}

const rockMaterial = new THREE.MeshStandardMaterial({
  color: 0xffffff,
  vertexColors: true,
  roughness: 0.62,
  metalness: 0.06,
  emissive: 0x121820,
  emissiveIntensity: 0.12,
  flatShading: true,
});
const islands = [];
for (const [x, z, scale, seed] of [
  [24, -36, 4.8, 2],
  [-30, -96, 7.2, 3],
  [36, -170, 4.4, 4],
  [-22, -250, 5.6, 5],
  [28, -340, 8.4, 6],
  [-38, -450, 5.2, 7],
  [22, -560, 6.4, 8],
]) {
  const geometry = rockGeometry(seed);
  geometry.computeBoundingBox();
  const mesh = new THREE.Mesh(geometry, rockMaterial);
  mesh.scale.setScalar(scale);
  mesh.position.set(x, -geometry.boundingBox.min.y * scale * 0.62, z);
  mesh.name = "decor";
  scene.add(mesh);
  islands.push(mesh);
}

const ringPool = [];
function createRing() {
  const torus = new THREE.Mesh(
    new THREE.TorusGeometry(1, 0.016, 12, 120),
    new THREE.MeshBasicMaterial({
      color: new THREE.Color().setRGB(1.45, 1.12, 0.62),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  const disc = new THREE.Mesh(
    new THREE.RingGeometry(0.84, 1, 48),
    new THREE.MeshBasicMaterial({
      color: 0xffe2b0,
      transparent: true,
      opacity: 0.08,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  const group = new THREE.Group();
  group.add(torus, disc);
  return group;
}
function makeRingMesh() {
  const group = createRing();
  group.visible = false;
  scene.add(group);
  ringPool.push(group);
  return group;
}

const shrine = new THREE.Group();
const shrineRing = createRing();
shrineRing.visible = true;
shrine.add(shrineRing);
for (const side of [-1, 1]) {
  const geometry = rockGeometry(20 + side);
  geometry.computeBoundingBox();
  const pillar = new THREE.Mesh(geometry, rockMaterial);
  pillar.scale.set(1.1, 4.6, 1.1);
  pillar.position.set(side * 4.4, 1.5, 0);
  shrine.add(pillar);
}
scene.add(shrine);

const bead = new THREE.InstancedMesh(
  new THREE.SphereGeometry(0.055, 8, 8),
  new THREE.MeshBasicMaterial({ color: 0xf7e7c6, transparent: true, opacity: 0.55 }),
  40,
);
bead.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
scene.add(bead);
const beadDummy = new THREE.Object3D();

const trailMax = 42;
const trail = [];
const trailGeo = new THREE.BufferGeometry();
const trailPos = new Float32Array(trailMax * 6);
const trailCol = new Float32Array(trailMax * 6);
trailGeo.setAttribute("position", new THREE.BufferAttribute(trailPos, 3));
trailGeo.setAttribute("color", new THREE.BufferAttribute(trailCol, 3));
const trailIndex = [];
for (let i = 0; i < trailMax - 1; i++) {
  const a = i * 2;
  trailIndex.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
}
trailGeo.setIndex(trailIndex);
const trailMesh = new THREE.Mesh(
  trailGeo,
  new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  }),
);
scene.add(trailMesh);

const sparkMax = 240;
const sparks = [];
const sparkGeo = new THREE.BufferGeometry();
const sparkPos = new Float32Array(sparkMax * 3);
const sparkCol = new Float32Array(sparkMax * 3);
sparkGeo.setAttribute("position", new THREE.BufferAttribute(sparkPos, 3));
sparkGeo.setAttribute("color", new THREE.BufferAttribute(sparkCol, 3));
const sparkPoints = new THREE.Points(
  sparkGeo,
  new THREE.PointsMaterial({
    size: 0.18,
    map: glowMap,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    vertexColors: true,
    sizeAttenuation: true,
  }),
);
scene.add(sparkPoints);

const moteBase = Array.from({ length: 48 }, () => ({
  x: (Math.random() - 0.5) * 22,
  y: 0.6 + Math.random() * 4.5,
  z: -Math.random() * 36,
  p: Math.random() * Math.PI * 2,
}));
const moteGeo = new THREE.BufferGeometry();
const motePos = new Float32Array(moteBase.length * 3);
moteGeo.setAttribute("position", new THREE.BufferAttribute(motePos, 3));
const motes = new THREE.Points(
  moteGeo,
  new THREE.PointsMaterial({
    size: 0.07,
    map: glowMap,
    color: 0xffe6bf,
    transparent: true,
    opacity: 0.75,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  }),
);
scene.add(motes);

function burst(x, y, z, color, count) {
  for (let i = 0; i < count; i++) {
    if (sparks.length >= sparkMax) sparks.shift();
    sparks.push({
      x,
      y,
      z,
      vx: (Math.random() - 0.5) * 2.4,
      vy: Math.random() * 1.6,
      vz: (Math.random() - 0.5) * 2.4,
      life: 0.45 + Math.random() * 0.45,
      max: 0.9,
      color,
    });
  }
}

let preview = buildRun("open", "open");
let run = null;
let state = "menu";
let holding = false;
let pointerSteer = 0;
let keyLeft = false;
let keyRight = false;
let blockHold = false;
let touchLift = false;
let touchOriginY = 0;
let visualT = 0;
let shake = 0;
let punch = 0;
let cardTimer = 0;
let cardReady = false;
let unlockLine = "";
let lampFade = 1;
let tuned = false;
let fps = 60;
let fpsFrames = 0;
let fpsStamp = 0;
const frameFails = [];

function activeRun() {
  return run || preview;
}

function applyWake() {
  wake = WAKES[wakeName()];
  globeMat.color.setRGB(wake.lamp[0], wake.lamp[1], wake.lamp[2]);
  halo.material.color.setRGB(wake.trail[0], wake.trail[1], wake.trail[2]);
  lamp.color.setRGB(Math.min(wake.lamp[0], 1), Math.min(wake.lamp[1], 1), Math.min(wake.lamp[2], 1));
  sea.material.uniforms.uLamp.value.setRGB(wake.trail[0], wake.trail[1], wake.trail[2]);
  const names = ["gold", "pearl", "aurora"].filter(wakeUnlocked);
  el.wake.hidden = names.length < 2;
  el.wake.textContent = wake.label + " wake";
}

function bestText() {
  const bits = [];
  if (save.best) bits.push("Best " + Math.round(save.best).toLocaleString("en-US"));
  if (save.daily && save.daily.day === todayKey() && save.daily.best) {
    bits.push("Today " + Math.round(save.daily.best).toLocaleString("en-US"));
  }
  if (!save.best) bits.push("A short sail. About a minute.");
  return bits.join("   ·   ");
}

function refreshBest() {
  el.best.textContent = bestText();
}

function commitScore() {
  if (!run || run.saved) return;
  run.saved = true;
  const hadPearl = !!save.pearl;
  const hadAurora = !!save.aurora;
  if (run.score > (save.best || 0)) save.best = Math.round(run.score);
  if (run.bestChain > (save.chainBest || 0)) save.chainBest = run.bestChain;
  if (run.shrineDone) save.pearl = true;
  if (run.bestChain >= 12) save.aurora = true;
  if (run.mode === "daily") {
    const day = todayKey();
    if (!save.daily || save.daily.day !== day) save.daily = { day, best: 0 };
    if (run.score > save.daily.best) save.daily.best = Math.round(run.score);
  }
  save.runs = (save.runs || 0) + 1;
  const lines = [];
  if (!hadPearl && save.pearl) lines.push("Pearl wake is yours.");
  if (!hadAurora && save.aurora) lines.push("Aurora wake is yours.");
  unlockLine = lines.join(" ");
  persist();
  refreshBest();
  applyWake();
}

function steerNow() {
  if (keyLeft && !keyRight) return -1;
  if (keyRight && !keyLeft) return 1;
  return pointerSteer;
}

function isTouchPointer(event) {
  return event.pointerType === "touch" || event.pointerType === "pen";
}

function readPointer(event) {
  const raw = (event.clientX / Math.max(1, window.innerWidth) - 0.5) * 2.2;
  pointerSteer = Math.abs(raw) < 0.08 ? 0 : THREE.MathUtils.clamp(raw, -1, 1);
  if (touchLift) {
    // Drag up from where the finger landed to rise; sideways alone stays level.
    const liftPx = touchOriginY - event.clientY;
    holding = liftPx > 16;
  }
}

function begin(mode) {
  Promise.resolve(audio.resume()).then(() => audio.startVoyage());
  const name = mode === "daily" ? "daily" : "open";
  run = buildRun(name, name === "daily" ? "daily-" + todayKey() : "open");
  trail.length = 0;
  state = "play";
  holding = false;
  pointerSteer = 0;
  touchLift = false;
  keyLeft = false;
  keyRight = false;
  cardReady = false;
  cardTimer = 0;
  unlockLine = "";
  show(el.title, false);
  show(el.card, false);
  show(el.paused, false);
  show(el.hud, true);
  el.pause.hidden = false;
  shrine.position.z = run.shrineZ;
}

function fillCard() {
  const madeIt = state === "arrived";
  el.kicker.textContent = madeIt ? "You made the crossing" : "The sea is still there";
  el.cardTitle.textContent = madeIt ? "The tide kept your song." : "The lantern went dark.";
  el.cardScore.textContent = Math.round(run.score).toLocaleString("en-US");
  const bits = ["Chain " + run.bestChain, run.perfects + " clear notes"];
  if (unlockLine) bits.push(unlockLine);
  el.detail.textContent = bits.join("   ·   ");
  el.further.hidden = !madeIt;
  el.hear.disabled = !run.notes.some((note) => note.grade !== "miss");
  el.copy.textContent = "Copy result";
  show(el.card, true);
  show(el.hud, false);
  el.pause.hidden = true;
}

function hintText() {
  if (state !== "play" || !run) return "";
  const passed = run.rings.filter((ring) => ring.done).length;
  if (passed >= 6) return "";
  const upcoming = nextRing(run);
  if (!upcoming) return "";
  const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const tail = fine ? "   ·   arrows" : "";
  const dx = upcoming.x - run.flight.x;
  const dy = upcoming.y - run.flight.y;
  const side = dx > 0.7 ? "Slide right" : dx < -0.7 ? "Slide left" : "";
  const riseWord = fine ? "Hold to rise" : "Drag up to rise";
  const glideWord = fine ? "Let go to glide" : "Ease down to glide";
  const vert = dy > 0.45 ? riseWord : dy < -0.45 ? glideWord : side ? "Steady" : "Steady";
  return (side ? vert + "   ·   " + side : vert) + tail;
}

function syncView(dt) {
  const current = activeRun();
  const flight = current.flight;
  const bob = Math.sin(visualT * 1.3) * 0.04;
  if (state === "menu") {
    boat.position.set(Math.sin(visualT * 0.35) * 0.2, START_Y + bob, START_Z);
    boat.rotation.x = Math.sin(visualT * 0.5) * 0.04;
    boat.rotation.z = Math.sin(visualT * 0.8) * 0.04;
  } else {
    boat.position.set(flight.x, flight.y + bob, flight.z);
    boat.rotation.x = THREE.MathUtils.clamp(flight.vy * 0.16, -0.35, 0.42);
    boat.rotation.z = THREE.MathUtils.clamp(-flight.vx * 0.12, -0.38, 0.38);
  }
  boat.rotation.y = 0;
  const height = boat.position.y;
  shadow.position.set(boat.position.x, 0.06, boat.position.z);
  shadow.material.opacity = Math.max(0.05, 0.3 - Math.max(0, height - 1.2) * 0.05);
  sea.position.x = boat.position.x;
  sea.position.z = boat.position.z;
  sea.material.uniforms.uTime.value = visualT;
  sea.material.uniforms.uBoat.value.set(boat.position.x, boat.position.z);
  const alive = state === "dark" ? lampFade : Math.max(0.25, (current.flame || 3) / 3);
  lamp.intensity = (0.45 + alive * 0.55) * (0.92 + Math.sin(visualT * 3.4) * 0.05);
  halo.scale.setScalar(0.48 + alive * 0.22 + Math.sin(visualT * 3.4) * 0.02);
  moonBody.position.copy(camera.position).addScaledVector(MOON, 360);
  moonMesh.position.copy(moonBody.position);
  stars.position.copy(camera.position);
  sky.position.copy(camera.position);
  sky.material.uniforms.uTime.value = visualT;
  sky.material.uniforms.uHymn.value = Math.min(1, (current.chain || 0) / 12);

  const upcoming = nextRing(current);
  let shown = 0;
  for (const ring of current.rings) {
    const ahead = flight.z - ring.z;
    if (ahead < -1.4 || ahead > 230) continue;
    const mesh = ringPool[shown] || makeRingMesh();
    shown += 1;
    mesh.visible = true;
    mesh.position.set(ring.x, ring.y, ring.z);
    const isNext = upcoming && upcoming.index === ring.index;
    const pulse = isNext ? 1 + Math.sin(visualT * 3.2) * 0.03 : 1;
    mesh.scale.setScalar(ring.r * pulse);
    mesh.rotation.z = visualT * 0.12 + ring.index * 0.2;
    const torus = mesh.children[0].material;
    const disc = mesh.children[1].material;
    if (ring.done) {
      torus.color.setRGB(0.32, 0.3, 0.28);
      disc.opacity = 0.025;
    } else if (isNext) {
      torus.color.setRGB(wake.trail[0], wake.trail[1], wake.trail[2]);
      disc.opacity = 0.1;
    } else {
      torus.color.setRGB(wake.trail[0] * 0.72, wake.trail[1] * 0.72, wake.trail[2] * 0.72);
      disc.opacity = 0.04;
    }
  }
  for (let i = shown; i < ringPool.length; i++) ringPool[i].visible = false;

  shrine.position.z = current.shrineZ;
  shrineRing.scale.setScalar(3.5);
  shrineRing.rotation.z = visualT * 0.18;
  shrineRing.children[0].material.color.setRGB(wake.lamp[0], wake.lamp[1], wake.lamp[2]);

  let beadsShown = 0;
  if (upcoming) {
    const stop = upcoming.z - BEAT_Z * 4;
    for (let z = flight.z - 8; z > stop && beadsShown < 40; z -= 7) {
      beadDummy.position.set(pathX(current, z), pathY(current, z), z);
      beadDummy.scale.setScalar(1);
      beadDummy.rotation.set(0, 0, 0);
      beadDummy.updateMatrix();
      bead.setMatrixAt(beadsShown, beadDummy.matrix);
      beadsShown += 1;
    }
  }
  bead.count = beadsShown;
  bead.instanceMatrix.needsUpdate = true;

  if (state === "play" && !reduceMotion) {
    trail.push({ x: boat.position.x, y: boat.position.y - 0.05, z: boat.position.z + 0.55 });
    if (trail.length > trailMax) trail.shift();
  } else if (trail.length) {
    trail.shift();
  }
  const count = trail.length;
  for (let i = 0; i < count; i++) {
    const sample = trail[i];
    const fade = count <= 1 ? 1 : i / (count - 1);
    const width = 0.05 + fade * 0.22;
    const o = i * 6;
    trailPos[o] = sample.x - width;
    trailPos[o + 1] = sample.y;
    trailPos[o + 2] = sample.z;
    trailPos[o + 3] = sample.x + width;
    trailPos[o + 4] = sample.y;
    trailPos[o + 5] = sample.z;
    for (const side of [0, 3]) {
      trailCol[o + side] = wake.trail[0] * fade;
      trailCol[o + side + 1] = wake.trail[1] * fade;
      trailCol[o + side + 2] = wake.trail[2] * fade;
    }
  }
  trailGeo.attributes.position.needsUpdate = true;
  trailGeo.attributes.color.needsUpdate = true;
  trailGeo.setDrawRange(0, Math.max(0, count - 1) * 6);

  for (let i = sparks.length - 1; i >= 0; i--) {
    const spark = sparks[i];
    spark.life -= dt;
    spark.x += spark.vx * dt;
    spark.y += spark.vy * dt;
    spark.z += spark.vz * dt;
    spark.vy -= dt * 0.4;
    if (spark.life <= 0) sparks.splice(i, 1);
  }
  for (let i = 0; i < sparks.length; i++) {
    const spark = sparks[i];
    sparkPos[i * 3] = spark.x;
    sparkPos[i * 3 + 1] = spark.y;
    sparkPos[i * 3 + 2] = spark.z;
    const fade = Math.max(0, spark.life / spark.max);
    sparkCol[i * 3] = spark.color[0] * fade;
    sparkCol[i * 3 + 1] = spark.color[1] * fade;
    sparkCol[i * 3 + 2] = spark.color[2] * fade;
  }
  sparkGeo.setDrawRange(0, sparks.length);
  sparkGeo.attributes.position.needsUpdate = true;
  sparkGeo.attributes.color.needsUpdate = true;

  for (let i = 0; i < moteBase.length; i++) {
    const mote = moteBase[i];
    motePos[i * 3] = mote.x + Math.sin(visualT * 0.35 + mote.p) * 0.35;
    motePos[i * 3 + 1] = mote.y + Math.sin(visualT * 0.55 + mote.p) * 0.18;
    motePos[i * 3 + 2] = boat.position.z + mote.z;
  }
  moteGeo.attributes.position.needsUpdate = true;

  if (run && run.endless) {
    for (const island of islands) {
      if (island.position.z > flight.z + 40) {
        island.position.z = flight.z - 200 - Math.random() * 120;
        island.position.x = (Math.random() < 0.5 ? -1 : 1) * (18 + Math.random() * 26);
      }
    }
  }
}

const camWish = new THREE.Vector3();
const lookWish = new THREE.Vector3();
const lookAt = new THREE.Vector3();

function updateCamera(dt) {
  const follow = 1 - Math.exp(-dt * 2.5);
  camWish.set(boat.position.x, boat.position.y + 2.55, boat.position.z + 4.9);
  lookWish.set(boat.position.x, boat.position.y + 0.55, boat.position.z - 8.2);
  if (state === "menu") {
    camWish.y += 1.15;
    camWish.z += 3.6;
    lookWish.y -= 0.55;
    if (!reduceMotion) camWish.x += Math.sin(visualT * 0.15) * 0.45;
  }
  camera.position.lerp(camWish, follow);
  lookAt.lerp(lookWish, follow);
  if (shake > 0 && !reduceMotion) {
    shake = Math.max(0, shake - dt);
    camera.position.x += (Math.random() - 0.5) * 0.12 * shake;
    camera.position.y += (Math.random() - 0.5) * 0.08 * shake;
  }
  camera.lookAt(lookAt);
  const baseFov = 47;
  punch = Math.max(0, punch - dt * 1.6);
  const nextFov = baseFov - punch * 1.4;
  if (Math.abs(camera.fov - nextFov) > 0.01) {
    camera.fov = nextFov;
    camera.updateProjectionMatrix();
  }
}

function updateHud() {
  if (!run || el.hud.hidden) return;
  const chain = run.chain > 1 ? " ×" + run.chain : "";
  el.score.textContent = Math.round(run.score).toLocaleString("en-US");
  el.chain.textContent = chain;
  el.pips.forEach((pip, index) => {
    pip.classList.toggle("on", run.flame > index + 0.2);
  });
  const hint = hintText();
  el.hint.hidden = !hint;
  el.hint.textContent = hint;
}

function onEvents(events) {
  for (const event of events) {
    if (event.type === "ring") {
      audio.ring(event.grade, event.freq, event.index);
      const color = event.grade === "miss" ? [0.7, 0.28, 0.22] : wake.trail;
      burst(event.x || 0, event.y, event.z, color, event.grade === "perfect" ? 26 : 10);
      if (event.grade === "perfect") punch = Math.min(1.6, punch + 1);
      if (event.grade === "miss" && !event.free) shake = 1;
    } else if (event.type === "kiss" || event.type === "plunge") {
      audio.splash();
      burst(run.flight.x, 0.3, run.flight.z, [0.75, 0.88, 1], 14);
      if (event.type === "plunge") shake = 1;
    } else if (event.type === "arrive") {
      audio.arrive();
      state = "arrived";
      cardTimer = 1.1;
      cardReady = false;
      commitScore();
      punch = 1.5;
    } else if (event.type === "die") {
      audio.stopVoyage();
      state = "dark";
      cardTimer = 0.75;
      cardReady = false;
      commitScore();
    }
  }
}

function frameCheck() {
  sea.updateMatrixWorld(true);
  const up = new THREE.Vector3(0, 0, 1).transformDirection(sea.matrixWorld);
  if (up.y < 0.9) frameFails.push("sea up " + up.y.toFixed(2));
  const hull = boat.getObjectByName("hull");
  const position = hull.geometry.attributes.position;
  let minZ = Infinity;
  for (let i = 0; i < position.count; i++) minZ = Math.min(minZ, position.getZ(i));
  const bow = boat.getObjectByName("bow");
  if (minZ > -1) frameFails.push("nose is not toward -z");
  if (Math.abs(minZ - bow.position.z) > 0.08) frameFails.push("bow marker disagrees with the hull");
}

function loop(now) {
  const dt = Math.min(0.033, (now - (loop.last || now)) / 1000);
  loop.last = now;
  visualT += dt;
  if (state === "play" && run) onEvents(stepRun(run, holding, dt, steerNow()));
  if (state === "dark") lampFade = Math.max(0, lampFade - dt * 1.3);
  else lampFade = 1;
  if ((state === "arrived" || state === "dark") && !cardReady) {
    cardTimer -= dt;
    if (cardTimer <= 0) {
      cardReady = true;
      fillCard();
    }
  }
  syncView(dt);
  updateCamera(dt);
  updateHud();
  composer.render();
  fpsFrames += 1;
  if (now - fpsStamp > 500) {
    fps = Math.round((fpsFrames * 1000) / (now - fpsStamp));
    fpsFrames = 0;
    fpsStamp = now;
    if (!tuned && visualT > 2.5 && fps > 0 && fps < 42) {
      renderer.setPixelRatio(1);
      bloom.strength = 0.22;
      tuned = true;
    }
  }
  if (visualT > 0.4) {
    document.body.dataset.probe = JSON.stringify(window.__lumen());
  }
  requestAnimationFrame(loop);
}

function resize() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  camera.aspect = width / Math.max(1, height);
  camera.updateProjectionMatrix();
  renderer.setSize(width, height, false);
  composer.setSize(width, height);
}

window.addEventListener("resize", resize);
window.addEventListener("pointerdown", (event) => {
  if (event.target?.closest?.("button")) return;
  if (state === "play") {
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      /* some browsers refuse capture on the canvas */
    }
    if (isTouchPointer(event)) {
      // Touch: contact alone steers; only dragging upward lifts.
      touchLift = true;
      touchOriginY = event.clientY;
      holding = false;
    } else {
      touchLift = false;
      holding = true;
    }
    readPointer(event);
  }
});
window.addEventListener("pointermove", (event) => {
  if (state !== "play") return;
  if (!touchLift && !holding) return;
  readPointer(event);
});
window.addEventListener("pointerup", (event) => {
  try {
    if (canvas.hasPointerCapture?.(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
  } catch {
    /* ignore */
  }
  holding = false;
  pointerSteer = 0;
  touchLift = false;
});
window.addEventListener("pointercancel", () => {
  holding = false;
  pointerSteer = 0;
  touchLift = false;
});
window.addEventListener("blur", () => {
  holding = false;
  pointerSteer = 0;
  touchLift = false;
  keyLeft = false;
  keyRight = false;
});
window.addEventListener("contextmenu", (event) => event.preventDefault());
window.addEventListener("keydown", (event) => {
  if (event.code === "Space") {
    event.preventDefault();
    if (event.repeat) return;
    if (state === "menu") {
      begin("open");
      blockHold = true;
      return;
    }
    if ((state === "arrived" || state === "dark") && cardReady) {
      begin(run.mode);
      blockHold = true;
      return;
    }
    if (state === "play" && !blockHold) holding = true;
  } else if (event.code === "Escape") {
    if (state === "play") {
      state = "pause";
      holding = false;
      pointerSteer = 0;
      touchLift = false;
      audio.holdVoyage();
      show(el.paused, true);
      el.pause.hidden = true;
    } else if (state === "pause") {
      state = "play";
      audio.resume();
      show(el.paused, false);
      el.pause.hidden = false;
    }
  } else if (event.code === "KeyM") {
    toggleMute();
  } else if (event.code === "ArrowLeft" || event.code === "KeyA") {
    keyLeft = true;
  } else if (event.code === "ArrowRight" || event.code === "KeyD") {
    keyRight = true;
  }
});
window.addEventListener("keyup", (event) => {
  if (event.code === "Space") {
    blockHold = false;
    holding = false;
  } else if (event.code === "ArrowLeft" || event.code === "KeyA") {
    keyLeft = false;
  } else if (event.code === "ArrowRight" || event.code === "KeyD") {
    keyRight = false;
  }
});

function toggleMute() {
  const next = !audio.isMuted();
  audio.setMuted(next);
  save.mute = next;
  persist();
  el.mute.dataset.muted = next ? "1" : "0";
  el.mute.setAttribute("aria-label", next ? "Sound off" : "Sound");
}

document.getElementById("set-out").addEventListener("click", () => begin("open"));
document.getElementById("daily").addEventListener("click", () => begin("daily"));
document.getElementById("again").addEventListener("click", () => begin(run ? run.mode : "open"));
document.getElementById("further").addEventListener("click", () => {
  if (!run || !run.shrineDone) return;
  Promise.resolve(audio.resume()).then(() => audio.startVoyage());
  run.saved = false;
  run.frozen = false;
  run.endless = true;
  state = "play";
  cardReady = false;
  show(el.card, false);
  show(el.hud, true);
  el.pause.hidden = false;
});
document.getElementById("hear").addEventListener("click", () => {
  audio.resume();
  if (run) audio.playSong(run.notes);
});
document.getElementById("copy").addEventListener("click", async () => {
  if (!run) return;
  const when = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const line = state === "arrived" ? "The tide kept your song." : "The lantern went dark.";
  const text = `Lumen Tide\n${when}\n${Math.round(run.score).toLocaleString("en-US")}\n${line}`;
  try {
    await navigator.clipboard.writeText(text);
    el.copy.textContent = "Copied";
  } catch {
    el.copy.textContent = "Could not copy";
  }
});
document.getElementById("resume").addEventListener("click", () => {
  if (state !== "pause") return;
  state = "play";
  audio.resume();
  show(el.paused, false);
  el.pause.hidden = false;
});
el.pause.addEventListener("click", () => {
  if (state !== "play") return;
  state = "pause";
  holding = false;
  pointerSteer = 0;
  touchLift = false;
  audio.holdVoyage();
  show(el.paused, true);
  el.pause.hidden = true;
});
el.mute.addEventListener("click", () => {
  audio.resume();
  toggleMute();
});
el.wake.addEventListener("click", () => {
  const names = ["gold", "pearl", "aurora"].filter(wakeUnlocked);
  const index = names.indexOf(wakeName());
  save.wake = names[(index + 1) % names.length];
  persist();
  applyWake();
});

function paintGrain() {
  const picture = document.createElement("canvas");
  picture.width = picture.height = 128;
  const pen = picture.getContext("2d");
  const image = pen.createImageData(128, 128);
  for (let i = 0; i < image.data.length; i += 4) {
    const shade = 170 + Math.random() * 85;
    image.data[i] = shade;
    image.data[i + 1] = shade;
    image.data[i + 2] = shade;
    image.data[i + 3] = 255;
  }
  pen.putImageData(image, 0, 0);
  const grain = document.getElementById("grain");
  grain.style.backgroundImage = `url(${picture.toDataURL()})`;
  grain.style.backgroundSize = "128px 128px";
}

window.__lumen = () => {
  const forward = new THREE.Vector3();
  camera.getWorldDirection(forward);
  return {
    state,
    z: boat.position.z,
    x: boat.position.x,
    y: boat.position.y,
    camZ: camera.position.z,
    fwdZ: forward.z,
    score: run ? Math.round(run.score) : 0,
    flame: run ? run.flame : 3,
    chain: run ? run.chain : 0,
    arrived: !!(run && run.arrived),
    dead: !!(run && run.dead),
    frameFails,
    fps,
  };
};
window.__lumenStart = (mode) => begin(mode || "open");
window.__lumenHold = (value) => {
  holding = !!value;
  touchLift = false;
};
window.__lumenSteer = (value) => {
  keyLeft = value < -0.2;
  keyRight = value > 0.2;
  pointerSteer = 0;
  touchLift = false;
};

applyWake();
if (save.mute) {
  audio.setMuted(true);
  el.mute.dataset.muted = "1";
}
refreshBest();
shrine.position.z = preview.shrineZ;
paintGrain();
boat.updateMatrixWorld(true);
frameCheck();
if (frameFails.length) document.title = "FAIL " + frameFails.join("; ");
resize();
const bootParams = new URLSearchParams(location.search);
if (bootParams.has("play")) {
  begin("open");
  holding = true;
}
requestAnimationFrame(loop);
